import type {
  ConfigureFamily,
  ConfigureOptionDto,
  ConfigureOptionsResponse,
} from "@/api/configure-options";
import type { RegionCode } from "@/api/filter";
import type { DocManualLineItem } from "@/api/quoteDocuments";
import type { SnapshotSelectedOptionInput } from "@/api/tasks";
import { applyAutoSelect } from "@/utils/configure-auto-select";
import {
  catalogItemsForFamily,
  getCustomToolingItem,
} from "@/utils/configure-catalog-display";
import {
  CUSTOM_TOOLING_PART_NO,
  applyUserSelectionOverrides,
  itemsToSelectionMap,
  loadProductSelection,
  manualLineItemsForCurrency,
  overridesToSelectionMap,
} from "@/utils/configure-storage";
import { parseDecimalString } from "@/utils/cost-display";
import {
  filterMdsCatalogItems,
  type MdsFilterState,
} from "@/utils/mds-configure";

// =============================================================================
// configure-selection-preview.ts — the option selection the Configure Solution
// modal shows on a fresh open (family recommendation with stored user overrides
// applied last),
// computed without mounting the modal.
//
// Needed because the RFQ popup can be opened straight from the Selector (navbar
// / floating "Request Support"), where the user picked a machine but never
// opened Configure. Both paths must freeze the SAME configuration into the task
// snapshot, so the hydration + row-building live here and the modal calls in.
// =============================================================================

export interface ConfigureSelectionRows {
  selectedPartNos: string[];
  selectedOptions: SnapshotSelectedOptionInput[];
  manualLineItems: DocManualLineItem[];
}

/** The modal's visible catalog rows (Profil element type + MDS filters applied). */
export function configureCatalogItems(args: {
  response: ConfigureOptionsResponse | undefined;
  family: ConfigureFamily | null;
  profilElementType: string | null;
  mdsFilters: MdsFilterState;
}): ConfigureOptionDto[] {
  const { response, family, profilElementType, mdsFilters } = args;
  if (!response?.available) return [];
  let items = catalogItemsForFamily(response.catalogItems, family);
  if (family === "profil_indi" && profilElementType) {
    items = items.filter((i) => i.elementType === profilElementType);
  }
  if (family === "mds") {
    items = filterMdsCatalogItems(items, mdsFilters);
  }
  return items;
}

/** Every currently valid checkbox row, including grouped/custom tooling. */
export function configureSelectablePartNos(args: {
  response: ConfigureOptionsResponse | undefined;
  family: ConfigureFamily | null;
  profilElementType: string | null;
  mdsFilters: MdsFilterState;
}): Set<string> {
  const partNos = new Set(
    configureCatalogItems(args).map((item) => item.partNo),
  );
  for (const group of args.response?.toolingGroups ?? []) {
    for (const item of group.items) partNos.add(item.partNo);
  }
  partNos.add(CUSTOM_TOOLING_PART_NO);
  return partNos;
}

/** Unit price per part-no across the catalog + tooling groups + custom tooling. */
export function configureUnitPrices(args: {
  response: ConfigureOptionsResponse | undefined;
  catalogItems: ReadonlyArray<ConfigureOptionDto>;
}): Map<string, number> {
  const map = new Map<string, number>();
  if (!args.response?.available) return map;
  const add = (partNo: string, price: string | null) => {
    const n = parseDecimalString(price);
    if (n != null && n >= 0) map.set(partNo, n);
  };
  for (const item of args.catalogItems) add(item.partNo, item.price);
  for (const group of args.response.toolingGroups ?? []) {
    for (const item of group.items) add(item.partNo, item.price);
  }
  const customToolingItem = getCustomToolingItem();
  add(customToolingItem.partNo, customToolingItem.price);
  return map;
}

/** Frozen snapshot/quote rows for a live selection map. */
export function buildSnapshotSelectedOptions(args: {
  response: ConfigureOptionsResponse | undefined;
  family: ConfigureFamily | null;
  selection: ReadonlyMap<string, number>;
  profilElementType: string | null;
  mdsFilters: MdsFilterState;
  customToolingText: string;
}): SnapshotSelectedOptionInput[] {
  const { response, selection, customToolingText } = args;
  const catalogItems = configureCatalogItems(args);
  const unitPriceByPartNo = configureUnitPrices({ response, catalogItems });
  const currency = response?.meta.currencyCode ?? "EUR";
  const customToolingItem = getCustomToolingItem();

  const all = new Map<string, ConfigureOptionDto>();
  for (const item of catalogItems) all.set(item.partNo, item);
  for (const group of response?.toolingGroups ?? []) {
    for (const item of group.items) all.set(item.partNo, item);
  }
  all.set(customToolingItem.partNo, customToolingItem);

  const out: SnapshotSelectedOptionInput[] = [];
  for (const [partNo, qty] of selection) {
    if (qty <= 0) continue;
    const item = all.get(partNo);
    out.push({
      partNo,
      qty,
      description: item?.description ?? null,
      category: item?.category ?? null,
      toolingType: item?.subCategory ?? null,
      itemName: item?.itemName ?? null,
      price: unitPriceByPartNo.get(partNo) ?? null,
      currency,
      // Keep an unused optional field absent (smaller snapshot + compatible
      // with the prior strict backend schema).
      ...(partNo === CUSTOM_TOOLING_PART_NO && customToolingText.trim()
        ? { requirements: customToolingText.trim() }
        : {}),
    });
  }
  return out;
}

/**
 * What Configure Solution would show for this machine right now: the user's
 * current family recommendation with explicit stored user edits applied last —
 * the same precedence the modal uses, so a direct RFQ freezes an identical
 * configuration and retains its workspace-local manual quote lines.
 */
export function previewConfigureSelection(args: {
  response: ConfigureOptionsResponse | undefined;
  productId: string;
  region: RegionCode;
  mspAutoFeedEnabled: boolean;
  requiredMachines: number;
}): ConfigureSelectionRows {
  const empty: ConfigureSelectionRows = {
    selectedPartNos: [],
    selectedOptions: [],
    manualLineItems: [],
  };
  const { response, productId } = args;
  if (!response?.available) return empty;

  const family: ConfigureFamily | null = response.meta.configureFamily ?? null;
  const stored = loadProductSelection(productId);
  const seeded = stored
    ? itemsToSelectionMap(stored.items)
    : new Map<string, number>();
  const profilElementType =
    stored?.profilElementType ?? response.meta.profilElementTypeDefault ?? null;
  const mdsFilters: MdsFilterState =
    family === "mds" && response.meta.mdsFilters
      ? {
          fastenerGroup:
            stored?.mdsFastenerGroup ?? response.meta.mdsFilters.fastenerGroup,
          feederTracks:
            stored?.mdsFeederTracks ?? response.meta.mdsFilters.feederTracks,
          forceKn: stored?.mdsForceKn ?? response.meta.mdsFilters.forceKn,
        }
      : { fastenerGroup: null, feederTracks: null, forceKn: null };

  const recommended = family
    ? applyAutoSelect({
        response,
        family,
        mspAutoFeedEnabled: args.mspAutoFeedEnabled,
        productId,
        region: args.region,
        requiredMachines: args.requiredMachines,
        mdsFilters,
        profilElementType,
        existing: new Map(),
      }).selection
    : new Map<string, number>();
  const selectablePartNos = configureSelectablePartNos({
    response,
    family,
    profilElementType,
    mdsFilters,
  });
  const selection = stored?.selectionFrozen
    ? new Map([...seeded].filter(([partNo]) => selectablePartNos.has(partNo)))
    : applyUserSelectionOverrides(
        recommended,
        overridesToSelectionMap(stored?.userOverrides),
        selectablePartNos,
      );

  return {
    selectedPartNos: Array.from(selection.entries())
      .filter(([, qty]) => qty > 0)
      .map(([partNo]) => partNo),
    selectedOptions: buildSnapshotSelectedOptions({
      response,
      family,
      selection,
      profilElementType,
      mdsFilters,
      customToolingText: stored?.customToolingText ?? "",
    }),
    manualLineItems: manualLineItemsForCurrency(
      stored?.manualLineItems,
      response.meta.currencyCode ?? "EUR",
    ),
  };
}
